import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import Toast from 'components/Toast';
import { useMembershipService } from 'services/memberships';
import {
  Membership as MembershipType,
  MembershipGender,
  MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS,
} from 'types/membership';
import { ROUTES } from 'utils/constants';
import { toYYYYMMDD } from 'utils/dateUtils';
import { ArrowLeft } from 'lucide-react';
import styles from '../Membership.module.scss';
import addStyles from '../AddMembership/AddMembership.module.scss';

function parseAddressParts(address: string | null | undefined): {
  street: string;
  barangayCity: string;
} {
  if (!address || !address.trim()) return { street: '', barangayCity: '' };
  const idx = address.indexOf(', ');
  if (idx === -1) return { street: address.trim(), barangayCity: '' };
  return {
    street: address.slice(0, idx).trim(),
    barangayCity: address.slice(idx + 2).trim(),
  };
}

const OCCUPATION_DROPDOWN_VALUES = [
  'Employed',
  'Self-Employed',
  'Unemployed',
  'Student',
  'Homemaker (ex. Housewife...)',
  'Retired',
  'Part-time Worker',
] as const;

function mapOldOccupationToDropdownValue(value: string): string {
  const raw = value.trim();
  if (!raw) return '';

  const normalized = raw.toLowerCase();
  const matched = OCCUPATION_DROPDOWN_VALUES.find(
    (option) => option.toLowerCase() === normalized,
  );

  return matched ?? raw;
}

const MEMBERSHIP_TYPE_DROPDOWN_VALUES = [
  'Pastor / Minister',
  'Board / Council (Administration)',
  'Elder / Leader',
  'Member (at least 1 year of attendance)',
  'Attender (less than 1 year of attendance)',
] as const;

function mapOldMembershipTypeToDropdownValue(value: string): string {
  const raw = value.trim();
  if (!raw) return '';

  // Backward-compatible mapping for membership records created before
  // the dropdown options were updated.
  switch (raw) {
    case 'Pastors/Ministers':
      return 'Pastor / Minister';
    case 'Administrators (Secretary, Finance Team...)':
      return 'Board / Council (Administration)';
    case 'Elder/Leader':
      return 'Elder / Leader';
    case 'Member (at least 1 year attendance)':
      return 'Member (at least 1 year of attendance)';
    case 'Attender (less than a year attendance)':
      return 'Attender (less than 1 year of attendance)';
    default:
      return raw;
  }
}

function EditMembership() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const membershipService = useMembershipService();
  const [membership, setMembership] = useState<MembershipType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [noMiddleName, setNoMiddleName] = useState(false);
  const [email, setEmail] = useState('');
  const [membershipDate, setMembershipDate] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [civilStatus, setCivilStatus] = useState('');
  const [departmentMinistry, setDepartmentMinistry] = useState('');
  const [nationality, setNationality] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressBarangayCity, setAddressBarangayCity] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employer, setEmployer] = useState('');
  const [membershipType, setMembershipType] = useState('');
  const [isSgl, setIsSgl] = useState(false);
  const [isSgm, setIsSgm] = useState(false);
  const [willingToWorkInTupad, setWillingToWorkInTupad] = useState(false);
  const [status, setStatus] = useState('active');
  const [notes, setNotes] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactNumber, setEmergencyContactNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const loadMembership = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await membershipService.getMembership(id);
      setMembership(data);
      setFirstName(data.firstName ?? '');
      setLastName(data.lastName ?? '');
      setMiddleName(data.middleName ?? '');
      setNoMiddleName(!data.middleName || data.middleName.trim() === '');
      setEmail(data.email ?? '');
      setMembershipDate(toYYYYMMDD(data.membershipDate ?? undefined) ?? '');
      setDateOfBirth(toYYYYMMDD(data.dateOfBirth ?? undefined) ?? '');
      setGender(data.gender ?? '');
      setCivilStatus(data.civilStatus ?? '');
      setDepartmentMinistry(data.departmentMinistry ?? '');
      setNationality(data.nationality ?? '');
      const { street, barangayCity } = parseAddressParts(data.address);
      setAddressStreet(street);
      setAddressBarangayCity(barangayCity);
      setContactNumber(data.contactNumber ?? '');
      setOccupation(mapOldOccupationToDropdownValue(data.occupation ?? ''));
      setEmployer(data.employer ?? '');
      setMembershipType(
        mapOldMembershipTypeToDropdownValue(data.membershipType ?? ''),
      );
      setIsSgl(!!data.isSgl);
      setIsSgm(!!data.isSgm);
      setWillingToWorkInTupad(!!data.willingToWorkInTupad);
      setStatus(data.status ?? 'active');
      setNotes(data.notes ?? '');
      setEmergencyContactName(data.emergencyContactName ?? '');
      setEmergencyContactNumber(data.emergencyContactNumber ?? '');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load membership.',
      );
    } finally {
      setLoading(false);
    }
  }, [id, membershipService]);

  useEffect(() => {
    void loadMembership();
  }, [loadMembership]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!id || !membership) return;
      if (membership.isVoided) {
        showToast('Cannot update a deleted membership.', 'error');
        return;
      }
      const membershipDateISO = membershipDate.includes('T')
        ? membershipDate
        : membershipDate
          ? `${membershipDate}T00:00:00.000Z`
          : undefined;
      const dateOfBirthISO =
        dateOfBirth && dateOfBirth.trim()
          ? dateOfBirth.includes('T')
            ? dateOfBirth
            : `${dateOfBirth}T00:00:00.000Z`
          : undefined;
      const address = [addressStreet.trim(), addressBarangayCity.trim()]
        .filter(Boolean)
        .join(', ');
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        middleName: noMiddleName ? undefined : middleName.trim() || undefined,
        email: email.trim() === '' ? '' : email.trim(),
        dateOfBirth: dateOfBirthISO,
        gender: (gender as MembershipGender) || undefined,
        civilStatus:
          (civilStatus as
            'single' | 'married' | 'widowed' | 'separated' | 'divorced') ||
          undefined,
        departmentMinistry: departmentMinistry.trim() || undefined,
        nationality: nationality.trim() || undefined,
        address: address || undefined,
        contactNumber: contactNumber.trim() || undefined,
        occupation: occupation.trim() || undefined,
        employer: employer.trim() || undefined,
        membershipDate: membershipDateISO,
        membershipType: membershipType.trim() || undefined,
        isSgl,
        isSgm,
        willingToWorkInTupad,
        status: (status as 'active' | 'inactive' | 'suspended') || 'active',
        notes: notes.trim() || undefined,
        emergencyContactName: emergencyContactName.trim() || undefined,
        emergencyContactNumber: emergencyContactNumber.trim() || undefined,
      };
      setIsSubmitting(true);
      try {
        await membershipService.updateMembership(id, payload);
        navigate(ROUTES.MEMBERSHIP.ROOT, {
          state: {
            toast: {
              message: 'Membership updated successfully.',
              type: 'success' as const,
            },
          },
        });
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to update membership.',
          'error',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      id,
      membership,
      firstName,
      lastName,
      middleName,
      noMiddleName,
      email,
      membershipDate,
      dateOfBirth,
      gender,
      civilStatus,
      departmentMinistry,
      nationality,
      addressStreet,
      addressBarangayCity,
      contactNumber,
      occupation,
      employer,
      membershipType,
      isSgl,
      isSgm,
      willingToWorkInTupad,
      status,
      notes,
      emergencyContactName,
      emergencyContactNumber,
      membershipService,
      navigate,
      showToast,
    ],
  );

  if (loading) {
    return (
      <div className={addStyles.container}>
        <div className={styles.loadingState}>Loading membership...</div>
      </div>
    );
  }

  if (error || !membership) {
    return (
      <div className={addStyles.container}>
        <header className={addStyles.header}>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
            className={addStyles.backButton}
            aria-label="Back to Membership"
          >
            <ArrowLeft size={20} strokeWidth={2} />
            Back
          </Button>
        </header>
        <div className={styles.errorState}>
          <p>{error ?? 'Membership not found.'}</p>
          <Button onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}>
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  if (membership.isVoided) {
    return (
      <div className={addStyles.container}>
        <header className={addStyles.header}>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
            className={addStyles.backButton}
            aria-label="Back to Membership"
          >
            <ArrowLeft size={20} strokeWidth={2} />
            Back
          </Button>
        </header>
        <div className={styles.errorState}>
          <p>Deleted memberships cannot be updated.</p>
          <Button onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}>
            Back to list
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={addStyles.container}>
      <header className={addStyles.header}>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
          className={addStyles.backButton}
          aria-label="Back to Membership"
        >
          <ArrowLeft size={20} strokeWidth={2} />
          Back
        </Button>
        <h1 className={addStyles.title}>Update membership</h1>
      </header>

      <div className={addStyles.formCard}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
        >
          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>First name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value.toUpperCase())}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Last name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value.toUpperCase())}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Middle name</Form.Label>
            <Form.Check
              type="checkbox"
              id="noMiddleName"
              label="Member has no middle name"
              checked={noMiddleName}
              onChange={(e) => {
                const checked = e.target.checked;
                setNoMiddleName(checked);
                if (checked) setMiddleName('');
              }}
              className={addStyles.checkbox}
            />
            <Form.Control
              type="text"
              placeholder="Middle name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value.toUpperCase())}
              className={styles.input}
              disabled={noMiddleName}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="member@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Membership date *</Form.Label>
            <DatePicker
              value={membershipDate}
              onChange={(v) => setMembershipDate(v ?? '')}
              className={styles.input}
              placeholder="mm/dd/yyyy"
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Date of birth</Form.Label>
            <DatePicker
              value={dateOfBirth}
              onChange={(v) => setDateOfBirth(v ?? '')}
              className={styles.input}
              placeholder="mm/dd/yyyy"
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Gender</Form.Label>
            <Form.Select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Civil status</Form.Label>
            <Form.Select
              value={civilStatus}
              onChange={(e) => setCivilStatus(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="widowed">Widowed</option>
              <option value="separated">Separated</option>
              <option value="divorced">Divorced</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Department/Ministry
            </Form.Label>
            <Form.Select
              value={departmentMinistry}
              onChange={(e) => setDepartmentMinistry(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              {MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Nationality</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex. Filipino"
              value={nationality}
              onChange={(e) => setNationality(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Blk. Lot/House number and Street name
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Blk. Lot/House number and Street name"
              value={addressStreet}
              onChange={(e) => setAddressStreet(e.target.value)}
              onBlur={(e) => setAddressStreet(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Barangay and City</Form.Label>
            <Form.Control
              type="text"
              placeholder="Barangay and City"
              value={addressBarangayCity}
              onChange={(e) => setAddressBarangayCity(e.target.value)}
              onBlur={(e) =>
                setAddressBarangayCity(e.target.value.toUpperCase())
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Contact number</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex. 09123456789"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Occupation</Form.Label>
            <Form.Select
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              {!OCCUPATION_DROPDOWN_VALUES.includes(
                occupation as (typeof OCCUPATION_DROPDOWN_VALUES)[number],
              ) &&
                occupation.trim() && (
                  <option value={occupation} disabled>
                    {occupation}
                  </option>
                )}
              <option value="Employed">Employed</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="Unemployed">Unemployed</option>
              <option value="Student">Student</option>
              <option value="Homemaker (ex. Housewife...)">
                Homemaker (ex. Housewife...)
              </option>
              <option value="Retired">Retired</option>
              <option value="Part-time Worker">Part-time Worker</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Employer / Company Name
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Employer / Company Name"
              value={employer}
              onChange={(e) => setEmployer(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Membership type</Form.Label>
            <Form.Select
              value={membershipType}
              onChange={(e) => setMembershipType(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              {!MEMBERSHIP_TYPE_DROPDOWN_VALUES.includes(
                membershipType as (typeof MEMBERSHIP_TYPE_DROPDOWN_VALUES)[number],
              ) &&
                membershipType.trim() && (
                  <option value={membershipType} disabled>
                    {membershipType}
                  </option>
                )}
              <option value="Pastor / Minister">Pastor / Minister</option>
              <option value="Board / Council (Administration)">
                Board / Council (Administration)
              </option>
              <option value="Elder / Leader">Elder / Leader</option>
              <option value="Member (at least 1 year of attendance)">
                Member (at least 1 year of attendance)
              </option>
              <option value="Attender (less than 1 year of attendance)">
                Attender (less than 1 year of attendance)
              </option>
            </Form.Select>
            <div className={styles.membershipRoleCheckboxes}>
              <Form.Check
                type="checkbox"
                id="edit-membership-is-sgl"
                label="SGL (Small Group Leader)"
                checked={isSgl}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setIsSgl(nextChecked);
                  if (nextChecked) setIsSgm(false);
                }}
                className={styles.membershipRoleCheckbox}
              />
              <Form.Check
                type="checkbox"
                id="edit-membership-is-sgm"
                label="SGM (Small Group Member)"
                checked={isSgm}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setIsSgm(nextChecked);
                  if (nextChecked) setIsSgl(false);
                }}
                className={styles.membershipRoleCheckbox}
              />
              <Form.Check
                type="checkbox"
                id="edit-membership-willing-to-work-in-tupad"
                label="willing to work in TUPAD"
                checked={willingToWorkInTupad}
                onChange={(e) => setWillingToWorkInTupad(e.target.checked)}
                className={styles.membershipRoleCheckbox}
              />
            </div>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Emergency contact name
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Emergency contact"
              value={emergencyContactName}
              onChange={(e) =>
                setEmergencyContactName(
                  e.target.value.replace(/\d/g, '').toUpperCase(),
                )
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Emergency contact number
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ex. 09123456789"
              value={emergencyContactNumber}
              onChange={(e) =>
                setEmergencyContactNumber(e.target.value.replace(/\D/g, ''))
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={styles.textarea}
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update membership'}
            </Button>
          </div>
        </Form>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default EditMembership;
